/**
 * London Smoke & Cure: transactional `lists/{listId}.recipients[]` writes for callers outside
 * Contact Manager (e.g. Newsletter Editor). Uses the same merge and removal guard as
 * `ContactManager.js` `saveRecipientsToList` (see `listWriteInvariants.js`). Optionally appends
 * a pre-write row under `lists/{listId}/recipient_snapshots`.
 */

import { doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import {
  LISTS_COLL,
  buildRecipientsToSaveForListDoc,
  assertNoAccidentalRecipientRemoval,
} from './contactManagerUtils';
import { appendRecipientSnapshotToTransaction } from './listRecipientSnapshot';
import { isListContactsSubcollectionMode } from './listContactsConstants';
import { saveListContactsSubcollection } from './listContactsService';

const FIRESTORE_RECIPIENTS_JSON_SOFT_LIMIT = 950000;

function isFirestorePermissionDenied(e) {
  const c = e?.code;
  return c === 'permission-denied' || c === 'PERMISSION_DENIED';
}

function recipientSnapshotsDisabledByEnv() {
  try {
    const v = process.env.REACT_APP_LIST_RECIPIENT_SNAPSHOTS_DISABLED;
    if (v == null || v === '') return false;
    const s = String(v).toLowerCase();
    return s === '1' || s === 'true' || s === 'yes';
  } catch {
    return false;
  }
}

/**
 * Read the latest list doc inside a transaction, merge `intendedRecipients` with server rows,
 * run the removal guard, then update `recipients` + timestamps.
 *
 * @param {string} listId
 * @param {object[]} intendedRecipients Full array the UI intends to persist.
 * @param {object} [options]
 * @param {boolean} [options.bypassRemovalGuard] Passed to {@link assertNoAccidentalRecipientRemoval}.
 * @param {boolean} [options.serverBaselineMerge=true] When false, builds save payload from `intendedRecipients` only (plus list settings chips), for restore-from-snapshot.
 * @param {boolean} [options.skipSnapshot] Skip `recipient_snapshots` write (tests or operator preference).
 * @param {string} [options.snapshotSource] e.g. `contact_manager`, `newsletter_editor`, `auth_register`, `restore_from_snapshot`.
 * @param {string|null} [options.snapshotActorUid]
 * @param {string|null} [options.snapshotActorEmail]
 */
export async function commitListRecipientsFirestoreTransactional(listId, intendedRecipients, options = {}) {
  const rows = Array.isArray(intendedRecipients) ? intendedRecipients : [];
  const ref = doc(db, LISTS_COLL, listId);
  const {
    bypassRemovalGuard,
    serverBaselineMerge = true,
    skipSnapshot = false,
    snapshotSource = 'unknown',
    snapshotActorUid = null,
    snapshotActorEmail = null,
  } = options;

  const preListSnap = await getDoc(ref);
  if (preListSnap.exists() && isListContactsSubcollectionMode(preListSnap.data()?.metadata)) {
    await saveListContactsSubcollection(listId, rows, {
      bypassRemovalGuard,
      serverBaselineMerge,
    });
    return;
  }

  const envSnapshotsOff = recipientSnapshotsDisabledByEnv();

  const runOnce = async (forceSkipSnapshot) => {
    const effectiveSkipSnapshot = forceSkipSnapshot || !!skipSnapshot || envSnapshotsOff;
    await runTransaction(db, async (transaction) => {
      const freshSnap = await transaction.get(ref);
      if (!freshSnap.exists()) {
        throw new Error(
          'London Smoke & Cure: this list no longer exists. Refresh Newsletter Editor or Contact Manager, then try again.',
        );
      }
      const freshData = freshSnap.data();
      const serverRecipients = Array.isArray(freshData.recipients) ? freshData.recipients : [];

      appendRecipientSnapshotToTransaction(transaction, listId, serverRecipients, {
        skipSnapshot: effectiveSkipSnapshot,
        source: snapshotSource,
        actorUid: snapshotActorUid,
        actorEmail: snapshotActorEmail,
      });

      const recipientsToSave = serverBaselineMerge
        ? buildRecipientsToSaveForListDoc(freshData, serverRecipients, rows)
        : buildRecipientsToSaveForListDoc(freshData, [], rows);

      if (serverRecipients.length) {
        assertNoAccidentalRecipientRemoval(serverRecipients, recipientsToSave, { bypassRemovalGuard });
      }
      const payloadJson = JSON.stringify(recipientsToSave);
      if (payloadJson.length > FIRESTORE_RECIPIENTS_JSON_SOFT_LIMIT) {
        throw new Error(
          'This save is too large for one Firestore list document (about 1MB limit). Nothing was written.',
        );
      }
      transaction.update(ref, {
        recipients: recipientsToSave,
        updatedAt: serverTimestamp(),
        'metadata.contactCount': recipientsToSave.length,
        'metadata.lastUpdated': serverTimestamp(),
      });
    });
  };

  try {
    await runOnce(false);
  } catch (e) {
    if (isFirestorePermissionDenied(e) && !skipSnapshot && !envSnapshotsOff) {
      console.warn(
        '[Contact Manager lists] Firestore denied a recipient_snapshots write (or related). Retrying list save without snapshots. Add rules for lists/{listId}/recipient_snapshots (see AI_AGENT_SERVICE_GUIDE), or set REACT_APP_LIST_RECIPIENT_SNAPSHOTS_DISABLED=true to silence snapshots.',
      );
      await runOnce(true);
    } else {
      throw e;
    }
  }
}
